import {Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/firestore';
import {Observable, zip} from 'rxjs';
import {Biowetter} from '../../model/vorhersagen/biowetter';
import {map, take} from 'rxjs/operators';
import {AngularFireStorage} from '@angular/fire/storage';
import {MesswertPaket} from '../../model/messwerte/messwert-paket';
import {Pollenflug} from '../../model/vorhersagen/pollenflug';
import {Unwetter} from '../../model/unwetter/unwetter';
import {RbbDocument} from '../../model/vorhersagen/rbb-document';

@Injectable()
export class FirebaseService {

  private readonly VORHERSAGE_HEUTE_24H_DEUTSCHLAND = 'vorhersagen_heute_24h_deutschland';
  private readonly VORHERSAGE_10_TAGE_24H_DEUTSCHLAND = 'vorhersagen_10tage_24h_deutschland';
  private readonly VORHERSAGE_10_TAGE_3H_DEUTSCHLAND = 'vorhersagen_10tage_3h_deutschland';
  private readonly VORHERSAGE_TERMINAL_BREMEN = 'vorhersagen_terminal_radiobremen_komplett';
  private readonly VORHERSAGE_TERMINAL_WDR = 'vorhersagen_terminal_wdr_komplett';
  private readonly VORHERSAGE_TERMINAL_RBB = 'vorhersagen_terminal_rbb_komplett';
  private readonly VORHERSAGE_TERMINAL_BR = 'vorhersagen_terminal_br_komplett';
  private readonly TERMINAL_RBB_DOCUMENT = 'V_TERM_RBB_AKTUELL';
  private readonly VORHERSAGE_UV_INDEX = 'uvindex_deutschland';
  private readonly VORHERSAGE_BIOWETTER_DEUTSCHLAND = 'biowetter_deutschland';
  private readonly VORHERSAGE_POLLENFLUG_DEUTSCHLAND = 'pollenflug_deutschland';
  private readonly MESSWERT_STUENDLICH_PAKET_5_PATH_PREFIX = 'messwerte_stuendlich_paket5_';
  private readonly MESSWERT_STUENDLICH_DWD_STRASSE = 'messwerte_stuendlich_DWD_STRASSE';
  private readonly MESSWERTE_METEOGROUP_RBB = 'messwerte_meteogroup_rbb';

  private readonly UNWETTER_BUNDESLAND = 'unwetter_bundesland';
  private readonly UNWETTER_LANDKREIS = 'unwetter_landkreise';

  constructor(private firestore: AngularFirestore, private firestorage: AngularFireStorage) {
  }

  // TODO: Anhand des Eingangsdatums nur die aktuellsten Daten laden

  public getVorhersagenDeutschlandHeute24h(bundesland: string[]) {
    const fieldPath = bundesland.includes('Deutschland') || bundesland.includes('Europa') ? 'ort.land' : 'ort.bundesLand';
    return this.firestore.collection(this.VORHERSAGE_HEUTE_24H_DEUTSCHLAND, ref =>
      ref.where(fieldPath, 'in', bundesland)).valueChanges();
  }

  public getVorhersagenDeutschland10Tage24hByID(id: string) {
    return this.firestore.collection(this.VORHERSAGE_10_TAGE_24H_DEUTSCHLAND, ref =>
      ref.where('ort.id', '==', id)).valueChanges();
  }

  public getVorhersagenDeutschland10Tage3hByID(id: string) {
    return this.firestore.collection(this.VORHERSAGE_10_TAGE_3H_DEUTSCHLAND, ref =>
      ref.where('ort.id', '==', id)).valueChanges();
  }


  public getMesswertStuendlich(pathSuffix: string, bundeslaender: string[], streetFilterActive?: boolean): Observable<MesswertPaket[]> {

    console.log('Streetfilter', streetFilterActive);

    const fieldPath = bundeslaender.includes('Deutschland') || bundeslaender.includes('Europa') ? 'ort.land' : 'ort.bundesLand';
    const path = streetFilterActive ? this.MESSWERT_STUENDLICH_DWD_STRASSE : `${this.MESSWERT_STUENDLICH_PAKET_5_PATH_PREFIX}${pathSuffix}`;

    return this.firestore.collection<MesswertPaket>(path
      , ref => ref.where(fieldPath, 'in', bundeslaender)
    ).valueChanges();
  }

  // .orderBy('messwerte.lufttemperatur.messzeitpunkt').startAt(filterDate)
  // where('messwerte.lufttemperatur.messzeitpunkt', opStrShowOldValues, dateNow.toUTCString()


  public getMesswerteStuendlichByID(globaldIds: string[], pathSuffix: string) {
    return this.firestore.collection<MesswertPaket>(`${this.MESSWERT_STUENDLICH_PAKET_5_PATH_PREFIX}${pathSuffix}`, ref => ref.where('ort.id', 'in', globaldIds)).valueChanges();
  }


  public getTerminalTexteRadioBremen() {
    return this.firestore.collection(this.VORHERSAGE_TERMINAL_BREMEN).get();
  }

  public getTerminalTexteWDR() {
    return this.firestore.collection(this.VORHERSAGE_TERMINAL_WDR).get();
  }

  public getTerminalTexteBR() {
    return this.firestore.collection(this.VORHERSAGE_TERMINAL_BR).get();
  }

  public getTerminalTexteRbb(): Observable<RbbDocument> {
    return this.firestore.collection<RbbDocument>(this.VORHERSAGE_TERMINAL_RBB)
      .doc<RbbDocument>(this.TERMINAL_RBB_DOCUMENT)
      .get()
      .pipe(take(1))
      .pipe(map(queryDocumentSnapshot => queryDocumentSnapshot.data()));
  }

  public getFileListFromStorage(storagePath: string, childPath) {
    return this.firestorage.ref(storagePath).child(childPath).listAll();
  }

  public getUvIndexDeutschland(bundeslaender: string[]) {
    return this.firestore.collection(this.VORHERSAGE_UV_INDEX, ref => ref.where('ort.bundesLand', 'in', bundeslaender)).get();
  }

  public getBiowetter(): Observable<Biowetter[]> {
    return this.firestore.collection<Biowetter>(this.VORHERSAGE_BIOWETTER_DEUTSCHLAND)
      .get()
      .pipe(map(querySnapshot =>
        querySnapshot.docs.map(queryDocumentSnapshot => queryDocumentSnapshot.data()))
      );
  }

  public getUnwetterBundesland(bundeslaender: string[]): Observable<Unwetter[]> {
    return this.getUnwetter(this.UNWETTER_BUNDESLAND, 'ort.ort', bundeslaender);
  }

  private getUnwetter(collection: string, fieldPathForFilter: string, bundeslaender: string[]): Observable<Unwetter[]> {
    if (bundeslaender.length > 10) {
      const firstBatch = bundeslaender.slice(0, 10);
      const secondBatch = bundeslaender.slice(10);
      return zip(this.getUnwetterForLessThanTenBundeslaender(collection, fieldPathForFilter, firstBatch),
        this.getUnwetterForLessThanTenBundeslaender(collection, fieldPathForFilter, secondBatch))
        .pipe(map(([firstResult, secondResult]) => [...firstResult, ...secondResult]));
    }
    return this.getUnwetterForLessThanTenBundeslaender(collection, fieldPathForFilter, bundeslaender);
  }

  private getUnwetterForLessThanTenBundeslaender(collection: string,
                                                 fieldPathForFilter: string,
                                                 bundeslaender: string[]): Observable<Unwetter[]> {
    return this.firestore.collection<Unwetter>(collection, ref => ref.where(fieldPathForFilter, 'in', bundeslaender))
      .get()
      .pipe(take(1))
      .pipe(map(querySnapshot => querySnapshot.docs.map(queryDocumentSnapshot => queryDocumentSnapshot.data())));
  }

  public getUnwetterLandkreis(bundeslaender: string[]): Observable<Unwetter[]> {
    return this.getUnwetter(this.UNWETTER_LANDKREIS, 'ort.bundesland', bundeslaender);
  }

  public getPollenflug(): Observable<Pollenflug[]> {
    return this.firestore.collection<Pollenflug>(this.VORHERSAGE_POLLENFLUG_DEUTSCHLAND)
      .get()
      .pipe(map(querySnapshot =>
        querySnapshot.docs.map(queryDocumentSnapshot => queryDocumentSnapshot.data()))
      );
  }

  public getAllMesswerteMeteoGroupRBB() {
    return this.firestore.collection(this.MESSWERTE_METEOGROUP_RBB).get();
  }

  public getMesswerteMeteoGroupRBBFor(doc: string) {
    return this.firestore.collection(this.MESSWERTE_METEOGROUP_RBB).doc(doc).get();
  }
}
